import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";

function extractErrorMessage(err) {
  const detail = err.response?.data?.detail;
  if (!detail) return "An unexpected error occurred.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg ?? JSON.stringify(e)).join("; ");
  }
  return JSON.stringify(detail);
}

function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function DatabaseBackupsPage() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const [restoreSuccess, setRestoreSuccess] = useState("");

  // Upload restore dialog
  const [showUploadRestore, setShowUploadRestore] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadRestoring, setUploadRestoring] = useState(false);
  const [uploadRestoreError, setUploadRestoreError] = useState("");
  const [uploadRestoreSuccess, setUploadRestoreSuccess] = useState("");
  const fileInputRef = useRef(null);

  const canCreate   = hasPerm("backup.create");
  const canDownload = hasPerm("backup.download");
  const canRestore  = hasPerm("backup.restore");

  useEffect(() => {
    if (!hasPerm("backup.view")) navigate("/dashboard", { replace: true });
  }, [hasPerm, navigate]);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/backups");
      setBackups(data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleCreateBackup = async () => {
    setCreating(true);
    setCreateError("");
    try {
      await api.post("/backups");
      await fetchBackups();
    } catch (err) {
      setCreateError(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await api.get(`/backups/${encodeURIComponent(filename)}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(`Failed to download "${filename}".`);
    }
  };

  const openRestoreConfirm = (filename) => {
    setConfirmRestore(filename);
    setRestoreError("");
    setRestoreSuccess("");
  };

  const handleRestoreConfirm = async () => {
    if (!confirmRestore) return;
    setRestoring(true);
    setRestoreError("");
    setRestoreSuccess("");
    try {
      await api.post(`/backups/${encodeURIComponent(confirmRestore)}/restore`);
      setRestoreSuccess("Database restored successfully.");
    } catch (err) {
      setRestoreError(extractErrorMessage(err));
    } finally {
      setRestoring(false);
    }
  };

  const closeRestoreDialog = () => {
    if (restoring) return;
    setConfirmRestore(null);
    setRestoreError("");
    setRestoreSuccess("");
  };

  // ── Upload Restore ─────────────────────────────────────────────────────────
  const openUploadRestore = () => {
    setUploadFile(null);
    setUploadRestoreError("");
    setUploadRestoreSuccess("");
    setShowUploadRestore(true);
    setTimeout(() => { if (fileInputRef.current) fileInputRef.current.value = ""; }, 0);
  };

  const handleUploadFileChange = (e) => {
    const f = e.target.files[0];
    if (f && !f.name.endsWith(".sql")) {
      setUploadRestoreError("Only .sql files are accepted.");
      setUploadFile(null);
    } else {
      setUploadRestoreError("");
      setUploadFile(f || null);
    }
  };

  const handleUploadRestore = async () => {
    if (!uploadFile) { setUploadRestoreError("Please select a .sql backup file."); return; }
    setUploadRestoring(true);
    setUploadRestoreError("");
    setUploadRestoreSuccess("");
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      await api.post("/backups/restore-upload", formData);
      setUploadRestoreSuccess("Database restored successfully.");
      setUploadFile(null);
      await fetchBackups();
    } catch (err) {
      setUploadRestoreError(extractErrorMessage(err));
    } finally {
      setUploadRestoring(false);
    }
  };

  const closeUploadRestore = () => {
    if (uploadRestoring) return;
    setShowUploadRestore(false);
    setUploadFile(null);
    setUploadRestoreError("");
    setUploadRestoreSuccess("");
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      <main className="flex-1 p-4 lg:p-6 max-w-[1100px] w-full mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-xl font-bold text-gray-800">Database Backups</h1>
          <div className="flex gap-2">
            {canCreate && (
              <button onClick={handleCreateBackup} disabled={creating}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded">
                {creating ? "Creating..." : "Create Backup"}
              </button>
            )}
            {canRestore && (
              <button onClick={openUploadRestore}
                className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded">
                Restore Backup
              </button>
            )}
          </div>
        </div>
        {createError && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3">{createError}</div>}
        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3">{error}</div>}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading backups...</div>
          ) : backups.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No backup files found. Click <strong>Create Backup</strong> to generate one.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">File Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Date Created</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">File Size</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {backups.map((b) => (
                  <tr key={b.filename} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-800 break-all">{b.filename}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(b.created_at)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatBytes(b.size_bytes)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {canDownload && (
                          <button onClick={() => handleDownload(b.filename)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium px-3 py-1.5 rounded border border-gray-300">
                            Download
                          </button>
                        )}
                        {canRestore && (
                          <button onClick={() => openRestoreConfirm(b.filename)}
                            className="bg-red-50 hover:bg-red-100 text-red-700 text-xs font-medium px-3 py-1.5 rounded border border-red-300">
                            Restore
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </main>

      {confirmRestore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-red-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-800">Restore Database</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3 text-sm text-yellow-800">
                <p className="font-semibold mb-1">Warning: Destructive Operation</p>
                <p>Restoring this backup will <strong>overwrite the current database contents</strong>. All data created after the backup date will be permanently lost. This action cannot be undone.</p>
              </div>
              <p className="text-sm text-gray-700">Selected file: <span className="font-mono font-medium">{confirmRestore}</span></p>
              {restoreError && <p className="text-sm text-red-600">{restoreError}</p>}
              {restoreSuccess && <p className="text-sm text-green-600 font-medium">{restoreSuccess}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={closeRestoreDialog} disabled={restoring}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">
                {restoreSuccess ? "Close" : "Cancel"}
              </button>
              {!restoreSuccess && (
                <button onClick={handleRestoreConfirm} disabled={restoring}
                  className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded">
                  {restoring ? "Restoring..." : "Yes, Restore Now"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Upload Restore Dialog ─────────────────────────────────────────── */}
      {showUploadRestore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-red-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-800">Restore from Upload</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3 text-sm text-yellow-800">
                <p className="font-semibold mb-1">⚠ Destructive Operation</p>
                <p>Restoring a backup will <strong>overwrite the current database contents</strong>. All data created after the backup date will be permanently lost. This action cannot be undone.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select backup file (.sql)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".sql"
                  onChange={handleUploadFileChange}
                  disabled={uploadRestoring}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 disabled:opacity-50"
                />
                {uploadFile && (
                  <p className="mt-1 text-xs text-gray-500">
                    Selected: <span className="font-mono">{uploadFile.name}</span> ({formatBytes(uploadFile.size)})
                  </p>
                )}
              </div>
              {uploadRestoreError && <p className="text-sm text-red-600">{uploadRestoreError}</p>}
              {uploadRestoreSuccess && <p className="text-sm text-green-600 font-medium">{uploadRestoreSuccess}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={closeUploadRestore} disabled={uploadRestoring}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">
                {uploadRestoreSuccess ? "Close" : "Cancel"}
              </button>
              {!uploadRestoreSuccess && (
                <button onClick={handleUploadRestore} disabled={uploadRestoring || !uploadFile}
                  className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded">
                  {uploadRestoring ? "Restoring..." : "Yes, Restore Now"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
